// Web Push for the daily reminder. On iPhone/iPad it works only in the app installed to the
// Home Screen (iOS/iPadOS 16.4+); Safari tabs have no PushManager. The service worker side is
// public/push-sw.js; the server side is api/src/routes/push.ts and api/src/cron.ts.
//
// Rules:
// - Notification.requestPermission() only from a tap: enablePush() calls it before any await.
// - Subscribe with userVisibleOnly + the Worker's VAPID key (GET /api/push/key).
// - Subscribe again when the server's key changed (compare applicationServerKey) or the
//   subscription expires within a day; on every app start (reconcilePush) re-register the
//   subscription with the API, which also covers a failed pushsubscriptionchange in the worker.
import type { PushTestResult } from '@valyrio/engine/api';
import { api, ApiError } from './api';

/** What Settings shows for this device. */
export type PushState =
  | 'unsupported' // no service worker / Push API / notifications in this browser
  | 'needs-install' // iPhone/iPad in a Safari tab: must be added to the Home Screen first
  | 'needs-update' // installed on iPhone/iPad but iOS is older than 16.4
  | 'denied' // the user blocked notifications (re-enable in system/browser settings)
  | 'off' // allowed or not asked yet, no subscription on this device
  | 'on'; // subscribed on this device

export interface PushEnvironment {
  serviceWorker: boolean;
  pushManager: boolean;
  notification: boolean;
  ios: boolean;
  standalone: boolean;
  permission: NotificationPermission | null;
  subscribed: boolean;
}

/** Pure: the state for an environment (tested in push.test.ts). */
export function derivePushState(e: PushEnvironment): PushState {
  const supported = e.serviceWorker && e.pushManager && e.notification;
  if (e.ios && !e.standalone) return 'needs-install';
  if (!supported) return e.ios ? 'needs-update' : 'unsupported';
  if (e.permission === 'denied') return 'denied';
  if (e.subscribed && e.permission === 'granted') return 'on';
  return 'off';
}

export function isIos(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
}

export function isStandalone(): boolean {
  return window.matchMedia?.('(display-mode: standalone)').matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function permission(): NotificationPermission | null {
  return 'Notification' in window ? Notification.permission : null;
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) return null;
  // `ready` never resolves without a registered worker (e.g. `vite` dev): ask first.
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  if (reg.active) return reg;
  // First visit: the worker is still installing. Wait for it, but not forever.
  return Promise.race([navigator.serviceWorker.ready, new Promise<null>((r) => setTimeout(() => r(null), 10_000))]);
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await registration();
  return (await reg?.pushManager.getSubscription()) ?? null;
}

export async function currentPushState(): Promise<PushState> {
  const supported = pushSupported();
  let subscribed = false;
  if (supported) subscribed = !!(await currentSubscription().catch(() => null));
  return derivePushState({
    serviceWorker: 'serviceWorker' in navigator,
    pushManager: 'PushManager' in window,
    notification: 'Notification' in window,
    ios: isIos(),
    standalone: isStandalone(),
    permission: permission(),
    subscribed,
  });
}

/** The endpoint of this device's subscription (to mark "this device" in test results). */
export async function currentEndpoint(): Promise<string | null> {
  if (!pushSupported()) return null;
  return (await currentSubscription().catch(() => null))?.endpoint ?? null;
}

export function urlBase64ToUint8Array(base64: string): Uint8Array<ArrayBuffer> {
  const padded = (base64 + '='.repeat((4 - (base64.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(padded);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function sameBytes(a: ArrayBuffer | ArrayBufferView | null | undefined, b: Uint8Array): boolean {
  if (!a) return false;
  const x = a instanceof ArrayBuffer ? new Uint8Array(a) : new Uint8Array(a.buffer, a.byteOffset, a.byteLength);
  if (x.length !== b.length) return false;
  for (let i = 0; i < x.length; i++) if (x[i] !== b[i]) return false;
  return true;
}

/** Renew when it expires within this long. */
export const RENEW_BEFORE_MS = 24 * 60 * 60 * 1000;

export interface SubscriptionLike {
  expirationTime: number | null;
  options?: { applicationServerKey?: ArrayBuffer | ArrayBufferView | null };
}

/**
 * Pure: must this subscription be replaced? Yes when it was made with a different VAPID key
 * than the server's (rotated keys: pushes would be refused) or expires within a day. A browser
 * that does not expose `options.applicationServerKey` is trusted.
 */
export function subscriptionNeedsRenewal(sub: SubscriptionLike, serverKey: Uint8Array, now = Date.now()): boolean {
  const key = sub.options?.applicationServerKey;
  if (key && !sameBytes(key, serverKey)) return true;
  return sub.expirationTime != null && sub.expirationTime - now < RENEW_BEFORE_MS;
}

// The user's choice for this device, so a subscription the browser dropped is renewed silently.
const INTENT_KEY = 'valyrio:push';
function setIntent(on: boolean): void {
  try {
    if (on) localStorage.setItem(INTENT_KEY, 'on');
    else localStorage.removeItem(INTENT_KEY);
  } catch {
    /* private mode */
  }
}
function intended(): boolean {
  try {
    return localStorage.getItem(INTENT_KEY) === 'on';
  } catch {
    return false;
  }
}

/**
 * Make sure this device has a current subscription registered with the API. Needs permission
 * already granted (no prompt here). Returns the subscription.
 */
async function ensureSubscription(reg: ServiceWorkerRegistration, opts: { create: boolean }): Promise<PushSubscription | null> {
  const { publicKey } = await api.pushKey();
  const key = urlBase64ToUint8Array(publicKey);
  let sub = await reg.pushManager.getSubscription();
  let stale: string | null = null;
  if (sub && subscriptionNeedsRenewal(sub, key)) {
    stale = sub.endpoint;
    await sub.unsubscribe().catch(() => false);
    sub = null;
  }
  if (!sub) {
    if (!opts.create && !stale) return null;
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
  }
  try {
    await api.pushSubscribe(sub.toJSON());
  } catch (err) {
    // 409: this endpoint is still registered to the other person (they used this browser before).
    // 410: the push service told the server this subscription is gone, though the browser still
    // holds it. Either way a fresh subscription (new endpoint) fixes it.
    if (!(err instanceof ApiError && (err.status === 409 || err.status === 410))) throw err;
    if (err.status === 409) stale = stale ?? sub.endpoint;
    await sub.unsubscribe().catch(() => false);
    sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key });
    await api.pushSubscribe(sub.toJSON());
  }
  if (stale && stale !== sub.endpoint) await api.pushUnsubscribe(stale).catch(() => undefined);
  return sub;
}

/**
 * Turn reminders on for this device. Call only from a tap/click handler: iOS asks for permission
 * only during a user gesture, so requestPermission() runs before anything is awaited.
 */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return currentPushState();
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return perm === 'denied' ? 'denied' : 'off';
  const reg = await registration();
  if (!reg) throw new Error('no service worker');
  await ensureSubscription(reg, { create: true });
  setIntent(true);
  return 'on';
}

/** Turn reminders off for this device: forget it on the server, then unsubscribe. */
export async function disablePush(): Promise<PushState> {
  setIntent(false);
  const sub = await currentSubscription().catch(() => null);
  if (sub) {
    await api.pushUnsubscribe(sub.endpoint).catch(() => undefined);
    await sub.unsubscribe().catch(() => false);
  }
  return currentPushState();
}

/**
 * On app start (signed in, online): re-register this device's subscription, renewing it when the
 * server's VAPID key changed or it is about to expire, or re-creating it when the browser dropped
 * it although the user turned reminders on here. Never prompts; never throws.
 */
export async function reconcilePush(): Promise<void> {
  try {
    if (!pushSupported() || Notification.permission !== 'granted') return;
    const reg = await registration();
    if (!reg) return;
    const has = !!(await reg.pushManager.getSubscription());
    if (!has && !intended()) return;
    const sub = await ensureSubscription(reg, { create: intended() });
    if (sub) setIntent(true);
  } catch {
    /* offline or not configured: try again next start */
  }
}

/**
 * Replace this device's subscription with a fresh one (after the test showed the push service
 * reported it gone). Needs permission already granted. Returns the new state.
 */
export async function renewPush(): Promise<PushState> {
  if (!pushSupported() || Notification.permission !== 'granted') return currentPushState();
  const reg = await registration();
  if (!reg) return currentPushState();
  const old = await reg.pushManager.getSubscription();
  await old?.unsubscribe().catch(() => false);
  await ensureSubscription(reg, { create: true });
  setIntent(true);
  return 'on';
}

/**
 * Seconds the server waits before sending the test push, so the person can leave the app first:
 * iOS does not present a notification for the web app that is on screen.
 */
export const TEST_DELAY_SECONDS = 5;

export function sendTestPush(): Promise<PushTestResult> {
  return api.pushTest(TEST_DELAY_SECONDS);
}
